<template>
    <!-- Breadcrumb -->
    <a class="mt-5 b_title">
        <div class="flex items-center">
            <router-link :to="{name: 'Dashboard'}" class="cursor-pointer text-emerald-700 hover:text-emerald-900 text-sm sm:text-base">
                <span>Dashboard</span>
            </router-link>
            &nbsp;
            <div class="flex items-center text-gray-600 text-sm sm:text-base">
                <span> / คำขอเบิกค่าเชื้อเพลิง</span>
            </div>
        </div>
    </a>

    <!-- Summary Cards -->
    <div class="grid gap-4 mt-5 mb-6 md:grid-cols-2 xl:grid-cols-6">
        <!-- Total Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickTotalCard"
            @keyup.enter="onClickTotalCard"
            class="flex items-center p-4 bg-blue-100 rounded-lg shadow-lg hover:bg-blue-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-blue-500 bg-blue-50 rounded-full">
                <i class="fas fa-file-alt w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">คำขอทั้งหมด</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.total }}</p>
                <p class="text-xs text-gray-500">{{ summary.total_amount }} บาท</p>
            </div>
        </div>
        
        <!-- Pending Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickStatusCard('1')"
            @keyup.enter="onClickStatusCard('1')"
            class="flex items-center p-4 bg-yellow-100 rounded-lg shadow-lg hover:bg-yellow-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-yellow-500 bg-yellow-50 rounded-full">
                <i class="fas fa-clock w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">รอตรวจสอบ</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.pending }}</p>
                <p class="text-xs text-gray-500">{{ summary.pending_amount }} บาท</p>
            </div>
        </div>
        
        <!-- Approved Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickStatusCard('2')"
            @keyup.enter="onClickStatusCard('2')"
            class="flex items-center p-4 bg-green-100 rounded-lg shadow-lg hover:bg-green-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-green-500 bg-green-50 rounded-full">
                <i class="fas fa-check-circle w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">อนุมัติ/รอโอน</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.approved }}</p>
                <p class="text-xs text-gray-500">{{ summary.approved_amount }} บาท</p>
                <p class="text-xs text-orange-600 font-medium mt-1">
                    <i class="fas fa-hourglass-half mr-1"></i>รอโอน: {{ summary.approved_not_paid || 0 }} รายการ ({{ summary.approved_not_paid_amount || '0.00' }} บาท)
                </p>
            </div>
        </div>

        <!-- Paid Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickStatusCard('4')"
            @keyup.enter="onClickStatusCard('4')"
            class="flex items-center p-4 bg-emerald-100 rounded-lg shadow-lg hover:bg-emerald-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-emerald-600 bg-emerald-50 rounded-full">
                <i class="fas fa-money-bill-wave w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">อนุมัติ/โอนแล้ว</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.paid }}</p>
                <p class="text-xs text-gray-500">{{ summary.paid_amount }} บาท</p>
            </div>
        </div>

        <!-- Evidence Needed Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickStatusCard('5')"
            @keyup.enter="onClickStatusCard('5')"
            class="flex items-center p-4 bg-amber-100 rounded-lg shadow-lg hover:bg-amber-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-amber-600 bg-amber-50 rounded-full">
                <i class="fas fa-question-circle w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">ขอหลักฐานเพิ่ม</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.evidence_needed || 0 }}</p>
                <p class="text-xs text-gray-500">{{ summary.evidence_needed_amount || '0.00' }} บาท</p>
            </div>
        </div>
        
        <!-- Rejected Requests -->
        <div
            role="button"
            tabindex="0"
            @click="onClickStatusCard('3')"
            @keyup.enter="onClickStatusCard('3')"
            class="flex items-center p-4 bg-red-100 rounded-lg shadow-lg hover:bg-red-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
            <div class="p-3 mr-4 text-red-500 bg-red-50 rounded-full">
                <i class="fas fa-times-circle w-5 h-5"></i>
            </div>
            <div>
                <p class="mb-1 text-sm font-medium text-gray-600">ไม่อนุมัติ</p>
                <p class="text-xl font-semibold text-gray-700">{{ summary.rejected }}</p>
                <p class="text-xs text-gray-500">{{ summary.rejected_amount || '0.00' }} บาท</p>    
            </div>
        </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="p-4 mb-6 bg-white rounded-lg shadow-md">
        <form @submit.prevent="submitSearch" class="space-y-4">
            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <!-- Search Input -->
                <div class="lg:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">ค้นหา</label>
                    <input 
                        type="text" 
                        v-model="keyword"
                        placeholder="เลขที่คำขอ, ชื่อองค์กร, ชื่อผู้ประสานงาน, เบอร์โทร"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                </div>
                
                <!-- Status Filter -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">สถานะ</label>
                    <select 
                        v-model="status"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                        <option value="all">ทั้งหมด</option>
                        <option value="1">รอตรวจสอบ</option>
                        <option value="2">อนุมัติ / รอโอน</option>
                        <option value="3">ไม่อนุมัติ</option>
                        <option value="4">อนุมัติ / โอนแล้ว</option>
                        <option value="5">ขอหลักฐานเพิ่ม</option>
                    </select>
                </div>
                
                <!-- Placeholder for alignment -->
                <div class="hidden lg:block"></div>
            </div>
            
            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <!-- From Date -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">วันที่ส่งคำขอ (ตั้งแต่)</label>
                    <input 
                        type="date" 
                        v-model="fromDate"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                </div>
                
                <!-- To Date -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">วันที่ส่งคำขอ (ถึง)</label>
                    <input 
                        type="date" 
                        v-model="toDate"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                </div>
                
                <!-- Buttons -->
                <div class="lg:col-span-2 flex items-end space-x-2">
                    <button 
                        type="submit"
                        class="px-6 py-2 text-white bg-emerald-600 rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
                    >
                        <i class="fas fa-search mr-2"></i>ค้นหา
                    </button>
                    <button 
                        type="button"
                        @click="resetSearch"
                        class="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                    >
                        <i class="fas fa-redo mr-2"></i>รีเซ็ต
                    </button>
                </div>
            </div>
        </form>
    </div>

    <!-- Table Section -->
    <div class="w-full overflow-hidden rounded-lg shadow-md bg-white">
        <div class="w-full overflow-x-auto">
            <!-- Desktop Table (hidden on mobile) -->
            <table class="hidden md:table w-full min-w-full whitespace-nowrap">
                <thead>
                    <tr class="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                        <th class="px-4 py-3">เลขที่คำขอ</th>
                        <th class="px-4 py-3">ชื่อองค์กร</th>
                        <th class="px-4 py-3 hidden lg:table-cell">จังหวัด</th>
                        <th class="px-4 py-3">ผู้ประสานงาน</th>
                        <th class="px-4 py-3 hidden lg:table-cell">เบอร์โทร</th>
                        <th class="px-4 py-3 hidden xl:table-cell">วันที่ส่งคำขอ</th>
                        <th class="px-4 py-3 hidden xl:table-cell">วันที่ลงพื้นที่</th>
                        <th class="px-4 py-3">จำนวนเงิน</th>
                        <th class="px-4 py-3">สถานะ</th>
                        <th class="px-4 py-3 text-center">จัดการ</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y">
                    <tr v-for="(item, index) in data" :key="index" class="text-gray-700 hover:bg-gray-50">
                        <td class="px-4 py-3 text-sm font-medium">
                            {{ item.request_code }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <div class="max-w-xs truncate" :title="item.organization_name">
                                {{ item.organization_name }}
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm hidden lg:table-cell">
                            {{ item.province_display }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ item.coordinator_name }}
                        </td>
                        <td class="px-4 py-3 text-sm hidden lg:table-cell">
                            {{ item.coordinator_phone }}
                        </td>
                        <td class="px-4 py-3 text-sm hidden xl:table-cell">
                            {{ item.created_at_formatted }}
                        </td>
                        <td class="px-4 py-3 text-sm hidden xl:table-cell">
                            {{ item.field_date_formatted }}
                        </td>
                        <td class="px-4 py-3 text-sm font-medium">
                            {{ item.total_amount_formatted }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <span :class="getStatusClass(item.status)" class="px-2 py-1 text-xs font-semibold rounded-full">
                                {{ getStatusLabel(item.status) }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-center">
                            <router-link 
                                :to="{ name: 'FloodReliefFuelRequestManage', params: { id: item.id } }"
                                class="text-emerald-600 hover:text-emerald-800 transition-colors"
                            >
                                <i class="fas fa-cog"></i>
                            </router-link>
                        </td>
                    </tr>
                    <tr v-if="data.length === 0">
                        <td colspan="10" class="px-4 py-8 text-center text-gray-500">
                            <i class="fas fa-inbox text-4xl mb-2"></i>
                            <p>ไม่พบข้อมูลคำขอ</p>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Mobile Card View (shown on mobile only) -->
            <div class="md:hidden">
                <div v-for="(item, index) in data" :key="index" class="p-4 border-b hover:bg-gray-50">
                    <div class="flex justify-between items-start mb-2">
                        <span class="text-sm font-bold text-emerald-600">{{ item.request_code }}</span>
                        <span :class="getStatusClass(item.status)" class="px-2 py-1 text-xs font-semibold rounded-full">
                            {{ getStatusLabel(item.status) }}
                        </span>
                    </div>
                    <div class="space-y-1 text-sm text-gray-600">
                        <p><span class="font-medium">องค์กร:</span> {{ item.organization_name }}</p>
                        <p><span class="font-medium">จังหวัด:</span> {{ item.province_display }}</p>
                        <p><span class="font-medium">ผู้ประสานงาน:</span> {{ item.coordinator_name }}</p>
                        <p><span class="font-medium">เบอร์โทร:</span> {{ item.coordinator_phone }}</p>
                        <div class="flex justify-between">
                            <p><span class="font-medium">วันที่ส่ง:</span> {{ item.created_at_formatted }}</p>
                            <p><span class="font-medium">ลงพื้นที่:</span> {{ item.field_date_formatted }}</p>
                        </div>
                        <div class="flex justify-between items-center pt-2">
                            <p class="font-bold text-gray-800">{{ item.total_amount_formatted }} บาท</p>
                            <router-link 
                                :to="{ name: 'FloodReliefFuelRequestManage', params: { id: item.id } }"
                                class="px-3 py-1 text-sm text-white bg-emerald-600 rounded hover:bg-emerald-700 transition-colors"
                            >
                                <i class="fas fa-cog mr-1"></i>จัดการ
                            </router-link>
                        </div>
                    </div>
                </div>
                <div v-if="data.length === 0" class="p-8 text-center text-gray-500">
                    <i class="fas fa-inbox text-4xl mb-2"></i>
                    <p>ไม่พบข้อมูลคำขอ</p>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div class="px-4 py-3 border-t bg-gray-50">
            <VueTailwindPagination
                :current="current_page"
                :total="total"
                :per-page="per_page"
                @page-changed="onPageClick($event)"
            />
        </div>
    </div>
</template>

<script>
import '@ocrv/vue-tailwind-pagination/dist/style.css'
import VueTailwindPagination from '@ocrv/vue-tailwind-pagination'
import { mapState } from 'vuex'

export default {
    name: 'FloodReliefFuelRequests',
    components: {
        VueTailwindPagination,
    },
    data() {
        return {
            keyword: '',
            status: 'all',
            fromDate: '',
            toDate: '',
            isSearching: false,
        }
    },
    computed: {
        ...mapState('floodReliefAdmin', [
            'current_page',
            'total',
            'per_page',
            'data',
            'summary',
        ]),
    },
    methods: {
        // Fetch initial data
        fetchData() {
            this.$Progress.start()
            
            // Fetch summary
            this.$store.dispatch('floodReliefAdmin/fetchSummary')
                .catch((err) => {
                    this.handleError(err)
                })
            
            // Fetch requests
            this.$store.dispatch('floodReliefAdmin/fetchRequests', { page_number: 1 })
                .then(() => {
                    this.$Progress.finish()
                })
                .catch((err) => {
                    this.handleError(err)
                    this.$Progress.fail()
                })
        },

        // Submit search
        submitSearch() {
            this.$Progress.start()
            this.isSearching = true
            
            this.$store.dispatch('floodReliefAdmin/searchRequests', {
                page_number: 1,
                keyword: this.keyword,
                status: this.status,
                from_date: this.fromDate,
                to_date: this.toDate,
            })
                .then(() => {
                    this.$Progress.finish()
                })
                .catch((err) => {
                    this.handleError(err)
                    this.$Progress.fail()
                })
        },

        // Reset search
        resetSearch() {
            this.keyword = ''
            this.status = 'all'
            this.fromDate = ''
            this.toDate = ''
            this.isSearching = false
            this.$store.dispatch('floodReliefAdmin/resetFilters')
            this.fetchData()
        },

        // Page change handler
        onPageClick(page) {
            this.$Progress.start()
            
            if (this.isSearching || this.keyword || this.status !== 'all' || this.fromDate || this.toDate) {
                this.$store.dispatch('floodReliefAdmin/searchRequests', {
                    page_number: page,
                    keyword: this.keyword,
                    status: this.status,
                    from_date: this.fromDate,
                    to_date: this.toDate,
                })
                    .then(() => {
                        this.$Progress.finish()
                    })
                    .catch((err) => {
                        this.handleError(err)
                        this.$Progress.fail()
                    })
            } else {
                this.$store.dispatch('floodReliefAdmin/fetchRequests', { page_number: page })
                    .then(() => {
                        this.$Progress.finish()
                    })
                    .catch((err) => {
                        this.handleError(err)
                        this.$Progress.fail()
                    })
            }
        },

        // Get status label
        getStatusLabel(status) {
            const labels = {
                1: 'รอตรวจสอบ',
                2: 'อนุมัติแล้ว',
                3: 'ไม่อนุมัติ',
                4: 'โอนแล้ว',
                5: 'ขอหลักฐานเพิ่ม',
            }
            return labels[status] || 'ไม่ทราบสถานะ'
        },

        // Get status class for styling
        getStatusClass(status) {
            const classes = {
                1: 'bg-yellow-100 text-yellow-800',
                2: 'bg-green-100 text-green-800',
                3: 'bg-red-100 text-red-800',
                4: 'bg-emerald-100 text-emerald-800',
                5: 'bg-amber-100 text-amber-800',
            }
            return classes[status] || 'bg-gray-100 text-gray-800'
        },

        // Click handlers for summary cards
        onClickTotalCard() {
            // Reset filters and fetch all
            this.keyword = ''
            this.status = 'all'
            this.fromDate = ''
            this.toDate = ''
            this.isSearching = false

            this.$Progress.start()
            this.$store.dispatch('floodReliefAdmin/fetchRequests', { page_number: 1 })
                .then(() => {
                    this.$Progress.finish()
                    // scroll to list
                    window.scrollTo({ top: 200, behavior: 'smooth' })
                })
                .catch((err) => {
                    this.handleError(err)
                    this.$Progress.fail()
                })
        },

        onClickStatusCard(statusValue) {
            // Apply status filter and run search
            this.keyword = ''
            this.status = statusValue
            this.fromDate = ''
            this.toDate = ''
            this.isSearching = true

            this.$Progress.start()
            this.$store.dispatch('floodReliefAdmin/searchRequests', {
                page_number: 1,
                keyword: this.keyword,
                status: this.status,
                from_date: this.fromDate,
                to_date: this.toDate,
            })
                .then(() => {
                    this.$Progress.finish()
                    window.scrollTo({ top: 200, behavior: 'smooth' })
                })
                .catch((err) => {
                    this.handleError(err)
                    this.$Progress.fail()
                })
        },

        // Handle API errors
        handleError(err) {
            if (err.response && err.response.status === 401) {
                this.$store.dispatch('authMember/unauthenticateResetting')
            }
            console.error('Error:', err)
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
</style>
